/**
 * Client + SEO sidebar panel.
 * - Client fields are project-wide state (`state.clientInfo`)
 * - SEO panel is per-page (`state.pages[*].seo`)
 */

import { createButton } from './components.js';
import { escapeHTML } from '../shared/html-escape.js';
import { reportError } from '../core/report-error.js';
import { store, setByPath } from '../core/state.js';
import { eventBus } from '../core/events.js';
import {
  normalizeClientInfo,
  CLIENT_LINK_BINDINGS,
  CLIENT_TEXT_BINDINGS
} from '../shared/client-info.js';
import {
  migrateAllSectionsInState,
  clientInfoToSourceProfile
} from '../shared/client-info-migrate.js';
import {
  refreshCompositionContextFromClientInfo,
  resyncComposition
} from '../shared/composition-skin.js';
import { showToast } from './shell.js';
import { SEOPanel } from './seo-panel.js';
import { pexels } from '../editing/adapters/pexels.js';
import { unsplash } from '../editing/adapters/unsplash.js';
import { pixabayPhotos } from '../editing/adapters/pixabay.js';

const CLIENT_FIELD_GROUPS = [
  {
    label: 'Contact',
    open: true,
    fields: [
      { label: 'Business Name', path: 'clientInfo.name', placeholder: 'Your business name' },
      { label: 'Tagline', path: 'clientInfo.tagline', placeholder: 'Short brand tagline' },
      { label: 'Email', path: 'clientInfo.email', placeholder: 'hello@example.com', type: 'email' },
      { label: 'Phone', path: 'clientInfo.phone', placeholder: '5555551234', type: 'tel' },
      {
        label: 'Phone (Formatted)',
        path: 'clientInfo.phoneFormatted',
        placeholder: '(555) 555-1234',
        type: 'tel'
      },
      { label: 'Domain', path: 'clientInfo.domain', placeholder: 'www.example.com' }
    ]
  },
  {
    label: 'Address',
    open: false,
    fields: [
      { label: 'Address Line 1', path: 'clientInfo.address.lineOne', placeholder: '123 Main St' },
      { label: 'Address Line 2', path: 'clientInfo.address.lineTwo', placeholder: 'Suite 100' },
      { label: 'City', path: 'clientInfo.address.city', placeholder: 'Dallas' },
      { label: 'State/County', path: 'clientInfo.address.county', placeholder: 'TX' },
      { label: 'ZIP/Postcode', path: 'clientInfo.address.postcode', placeholder: '75001' },
      {
        label: 'Map Link',
        path: 'clientInfo.address.mapLink',
        placeholder: 'https://maps.google.com',
        type: 'url'
      }
    ]
  },
  {
    label: 'Social Links',
    open: false,
    fields: [
      {
        label: 'Facebook',
        path: 'clientInfo.social.facebook',
        placeholder: 'https://facebook.com/yourpage',
        type: 'url'
      },
      {
        label: 'Instagram',
        path: 'clientInfo.social.instagram',
        placeholder: 'https://instagram.com/yourhandle',
        type: 'url'
      },
      {
        label: 'LinkedIn',
        path: 'clientInfo.social.linkedin',
        placeholder: 'https://linkedin.com/company/yourcompany',
        type: 'url'
      },
      {
        label: 'X',
        path: 'clientInfo.social.x',
        placeholder: 'https://x.com/yourhandle',
        type: 'url'
      },
      {
        label: 'YouTube',
        path: 'clientInfo.social.youtube',
        placeholder: 'https://youtube.com/@yourchannel',
        type: 'url'
      },
      {
        label: 'Yelp',
        path: 'clientInfo.social.yelp',
        placeholder: 'https://yelp.com/biz/your-business',
        type: 'url'
      }
    ]
  }
];

const IMAGE_SOURCE_SETTINGS = [
  { key: 'pexels', label: 'Pexels', adapter: pexels, placeholder: 'Pexels API key' },
  { key: 'unsplash', label: 'Unsplash', adapter: unsplash, placeholder: 'Unsplash access key' },
  { key: 'pixabay', label: 'Pixabay', adapter: pixabayPhotos, placeholder: 'Pixabay API key' }
];

function getByPath(obj, path) {
  const keys = path.split('.');
  let current = obj;
  for (const key of keys) {
    if (current == null) return '';
    current = current[key];
  }
  return current == null ? '' : String(current);
}

export class ClientSeoPanel {
  constructor(container) {
    this._container = container;
    this._destroyed = false;
    this._seoPanel = null;
    this._render();
    this._listen();
  }

  _getClientInfo() {
    const state = store.getState();
    const projectName = state?.projectName || 'Untitled';
    return normalizeClientInfo(state?.clientInfo, projectName);
  }

  _hasSeoState() {
    const state = store.getState();
    return !!(
      state &&
      Array.isArray(state.pages) &&
      state.pages.length > 0 &&
      typeof state.activePage === 'string'
    );
  }

  _render() {
    const client = this._getClientInfo();
    const state = store.getState();
    const projectName = state?.projectName || 'Untitled';
    const pages = Array.isArray(state?.pages) ? state.pages : [];
    const activePageId = state?.activePage || pages[0]?.id || '';
    const imageSourceRows = IMAGE_SOURCE_SETTINGS.map((source) => {
      const value = source.adapter.getApiKey();
      const saved = !!value;
      return `
        <div class="pb-override-row pb-image-source-row" data-image-source-row="${escapeHTML(source.key)}" data-saved="${saved ? 'true' : 'false'}">
          <label class="pb-props-label" for="pb-image-source-${escapeHTML(source.key)}">${escapeHTML(source.label)}</label>
          <div class="pb-image-source-field">
            <input
              type="password"
              class="pb-input"
              id="pb-image-source-${escapeHTML(source.key)}"
              data-image-source-key="${escapeHTML(source.key)}"
              value="${escapeHTML(value)}"
              placeholder="${escapeHTML(source.placeholder)}"
              autocomplete="off"
              spellcheck="false"
            />
            <span data-image-source-clear="${escapeHTML(source.key)}"></span>
          </div>
          <small class="pb-image-source-status" data-image-source-status="${escapeHTML(source.key)}">${saved ? 'Saved for this account' : 'Not set'}</small>
        </div>
      `;
    }).join('');

    // Phase 8 — three pill-headed sub-zones (Sitewide Client / This page SEO /
    // Sitewide Integrations) with dev-reference bindings collapsed into a
    // single disclosure under Integrations.
    this._container.innerHTML = `
      <div class="pb-project-zone" data-zone="client">
        <header class="pb-project-zone-header">
          <span class="pb-project-zone-pill pb-project-zone-pill--sitewide">Sitewide</span>
          <h3 class="pb-project-zone-title">Client info</h3>
        </header>
        <p class="pb-props-hint">Single source of truth used by 11ty <code>client.json</code> and optional link bindings.</p>
        ${CLIENT_FIELD_GROUPS.map(
          (group) => `
          <section class="pb-project-group" aria-labelledby="pb-project-group-${escapeHTML(group.label.replace(/\\s+/g, '-').toLowerCase())}">
            <h4 class="pb-project-group-title" id="pb-project-group-${escapeHTML(group.label.replace(/\\s+/g, '-').toLowerCase())}">${group.label}</h4>
            <div class="pb-project-group-body">
              ${group.fields
                .map(
                  (field) => `
                <div class="pb-override-row">
                  <label class="pb-props-label">${field.label}</label>
                  <input
                    type="${field.type || 'text'}"
                    class="pb-input"
                    data-client-path="${field.path}"
                    value="${escapeHTML(getByPath(client, field.path.replace(/^clientInfo\./, '')))}"
                    placeholder="${escapeHTML(field.path === 'clientInfo.name' ? projectName : field.placeholder || '')}"
                    spellcheck="false"
                  />
                </div>
              `
                )
                .join('')}
            </div>
          </section>
        `
        ).join('')}
      </div>

      <div class="pb-project-zone" data-zone="seo">
        <header class="pb-project-zone-header">
          <span class="pb-project-zone-pill pb-project-zone-pill--page">This page</span>
          <h3 class="pb-project-zone-title">SEO</h3>
          ${
            pages.length > 1
              ? `
            <label class="pb-project-page-chip" for="pb-project-page-select">
              <span class="pb-sr-only">Edit SEO for page</span>
              <select id="pb-project-page-select" class="pb-project-page-chip-select" data-action="pb-project-page">
                ${pages.map((p) => `<option value="${escapeHTML(p.id)}"${p.id === activePageId ? ' selected' : ''}>${escapeHTML(p.name || p.id)}</option>`).join('')}
              </select>
            </label>`
              : ''
          }
        </header>
        <div id="pb-client-seo-mount"></div>
      </div>

      <div class="pb-project-zone" data-zone="integrations">
        <header class="pb-project-zone-header">
          <span class="pb-project-zone-pill pb-project-zone-pill--sitewide">Sitewide</span>
          <h3 class="pb-project-zone-title">Integrations</h3>
        </header>
        <section class="pb-project-group">
          <h4 class="pb-project-group-title">Analytics</h4>
          <div class="pb-project-group-body">
            <div class="pb-override-row">
              <label class="pb-props-label" for="pb-analytics-website-id">Umami Website ID</label>
              <input
                type="text"
                class="pb-input"
                id="pb-analytics-website-id"
                data-state-path="deploy.analyticsWebsiteId"
                value="${escapeHTML(getByPath(state, 'deploy.analyticsWebsiteId'))}"
                placeholder="From umami.sidecarr.com"
                autocomplete="off"
                spellcheck="false"
              />
              <small class="pb-props-hint">Every export reports to <code>umami.sidecarr.com</code>. Leave blank and the export ships a placeholder for you to replace after deploying.</small>
            </div>
          </div>
        </section>
        <section class="pb-project-group">
          <h4 class="pb-project-group-title">Deployment</h4>
          <div class="pb-project-group-body">
            <div class="pb-override-row">
              <label class="pb-props-label" for="pb-deploy-site-domain">Custom domain</label>
              <input
                type="text"
                class="pb-input"
                id="pb-deploy-site-domain"
                data-state-path="deploy.siteDomain"
                value="${escapeHTML(getByPath(state, 'deploy.siteDomain'))}"
                placeholder="example.com"
                autocomplete="off"
                spellcheck="false"
              />
              <small class="pb-props-hint">Where the site will actually live. Used for canonical URLs, the analytics domain pin, and the deploy tooling. Leave blank and the export assumes <code>&lt;project&gt;.pages.dev</code>.</small>
            </div>
            <div class="pb-override-row">
              <label class="pb-props-label" for="pb-deploy-github-repo">GitHub repository</label>
              <input
                type="text"
                class="pb-input"
                id="pb-deploy-github-repo"
                data-state-path="deploy.githubRepo"
                value="${escapeHTML(getByPath(state, 'deploy.githubRepo'))}"
                placeholder="owner/repo"
                autocomplete="off"
                spellcheck="false"
              />
              <small class="pb-props-hint">Where the exported code will be pushed. Fills in the CMS backend so editors publish to the right repo; leave blank and the export ships <code>CHANGE_ME_OWNER</code> for you to fix by hand.</small>
            </div>
          </div>
        </section>
        <section class="pb-project-group pb-project-image-sources" data-image-source-settings>
          <h4 class="pb-project-group-title">Image sources</h4>
          <div class="pb-project-group-body">
            ${imageSourceRows}
          </div>
        </section>
        <p class="pb-props-hint">Scan every section and bind matching phone, email, address, and social URLs to the fields above. Re-runnable; only changes hardcoded values.</p>
        <span data-action="pb-resync-client"></span>

        <!-- CMD-8. The ⋮ menu's last two survivors. Both stay createModal
             dialogs; only the entry point moves, and it moves HERE because
             head/body code and the site-wide link table are project-level
             settings, not editing verbs — which is why they never belonged in
             a menu beside "Take the tour" in the first place. Links also has a
             route in row 2's page pane; this is its settings home. -->
        <section class="pb-project-group">
          <h4 class="pb-project-group-title">Advanced</h4>
          <div class="pb-project-group-body pb-project-advanced">
            <span data-action="pb-open-code-injection"></span>
            <span data-action="pb-open-link-manager"></span>
          </div>
        </section>

        <details class="pb-project-dev-reference">
          <summary class="pb-project-dev-reference-summary">
            <span>Developer reference</span>
            <span class="pb-project-dev-reference-chevron" aria-hidden="true">›</span>
          </summary>
          <div class="pb-project-dev-reference-body">
            <section class="pb-client-links">
              <h4 class="pb-project-group-title">Link bindings</h4>
              <p class="pb-props-hint">Link fields can bind to these markers instead of hardcoded URLs.</p>
              <div class="pb-client-link-list">
                ${CLIENT_LINK_BINDINGS.map(
                  (binding) => `
                  <div class="pb-client-link-row">
                    <span>${binding.label}</span>
                    <code>pb-client://${binding.key}</code>
                  </div>
                `
                ).join('')}
              </div>
            </section>
            <section class="pb-client-links">
              <h4 class="pb-project-group-title">Text bindings</h4>
              <p class="pb-props-hint">Wrap display text in a span with these data attributes to bind it to a Client field.</p>
              <div class="pb-client-link-list">
                ${CLIENT_TEXT_BINDINGS.map(
                  (binding) => `
                  <div class="pb-client-link-row">
                    <span>${binding.label}</span>
                    <code>data-pb-client="${binding.key}"</code>
                  </div>
                `
                ).join('')}
              </div>
            </section>
          </div>
        </details>
      </div>
    `;

    this._mountKitButtons();
    this._groupByTask();
    this._seoMount = this._container.querySelector('#pb-client-seo-mount');
    this._bindClientInputs();
    this._bindStateInputs();
    this._bindResyncButton();
    this._bindAdvancedButtons();
    this._bindImageSourceSettings();
    this._bindPageSelector();
    this._ensureSeoPanel();
  }

  _mountKitButtons() {
    for (const [action, label, variant] of [
      ['pb-resync-client', 'Resync sections from Client Info', 'primary'],
      ['pb-open-code-injection', 'Code injection…', 'ghost'],
      ['pb-open-link-manager', 'Links…', 'ghost']
    ]) {
      this._container
        .querySelector(`[data-action="${action}"]`)
        .replaceWith(createButton({ label, variant, dataset: { action } }));
    }
    for (const source of IMAGE_SOURCE_SETTINGS) {
      this._container.querySelector(`[data-image-source-clear="${source.key}"]`).replaceWith(
        createButton({
          label: 'Clear',
          variant: 'ghost',
          size: 'sm',
          ariaLabel: `Clear ${source.label} API key`,
          dataset: { imageSourceClear: source.key }
        })
      );
    }
  }

  _taskGroup(key, label, nodes, consequence, defaultOpen = false) {
    const details = document.createElement('details');
    details.className = 'pb-project-task';
    details.dataset.projectTask = key;
    let saved = null;
    try {
      saved = localStorage.getItem(`pb:project-task:${key}`);
    } catch {
      /* Expected: privacy settings may disable presentation preferences. */
    }
    details.open = saved === null ? defaultOpen : saved === 'open';
    const summary = document.createElement('summary');
    summary.className = 'pb-project-task-summary';
    summary.textContent = label;
    const body = document.createElement('div');
    body.className = 'pb-project-task-body';
    const hint = document.createElement('p');
    hint.className = 'pb-props-hint';
    hint.textContent = consequence;
    body.append(hint, ...nodes.filter(Boolean));
    details.append(summary, body);
    let lastOpen = details.open;
    details.addEventListener('toggle', () => {
      if (details.open === lastOpen) return;
      lastOpen = details.open;
      try {
        localStorage.setItem(`pb:project-task:${key}`, details.open ? 'open' : 'closed');
      } catch (error) {
        // Expected: disabled storage must not prevent opening a settings group.
        reportError('Project panel preferences', error, { user: 'silent' });
      }
    });
    return details;
  }

  _groupByTask() {
    const client = this._container.querySelector('[data-zone="client"]');
    const clientGroup = (name) =>
      [...client.querySelectorAll(':scope > section.pb-project-group')].find(
        (group) => group.querySelector('h4')?.textContent === name
      );
    const business = this._taskGroup(
      'business',
      'Business details',
      [clientGroup('Contact'), clientGroup('Address')],
      'Used across the site wherever business details are bound. Resync in Advanced to update matching hardcoded details.',
      true
    );
    const links = this._taskGroup(
      'links',
      'Links and social',
      [clientGroup('Social Links')],
      'Bound social links update throughout the site. Empty fields leave those destinations unset.'
    );
    client.replaceChildren(business, links);

    const seo = this._container.querySelector('[data-zone="seo"]');
    const pageSelector = seo.querySelector('#pb-project-page-select')?.closest('label');
    const seoTask = this._taskGroup(
      'seo',
      'This page: SEO',
      [pageSelector, seo.querySelector('#pb-client-seo-mount')],
      'Search titles, descriptions and social previews apply to the selected page. Changing the page here also changes the canvas.',
      true
    );
    seo.replaceChildren(seoTask);

    const integrations = this._container.querySelector('[data-zone="integrations"]');
    const integrationGroup = (name) =>
      [...integrations.querySelectorAll(':scope > section.pb-project-group')].find(
        (group) => group.querySelector('h4')?.textContent === name
      );
    const publishing = this._taskGroup(
      'publishing',
      'Publishing',
      [integrationGroup('Analytics'), integrationGroup('Deployment')],
      'These values configure exported sites. Changes take effect when you publish or export again.'
    );
    integrations.querySelector(':scope > header')?.remove();
    const advanced = this._taskGroup(
      'advanced',
      'Advanced',
      [...integrations.childNodes],
      'Image-source keys stay with this account. Resync changes matching section content; code injection runs in the exported site.'
    );
    integrations.replaceChildren(publishing, advanced);
  }

  _openTaskFor(element) {
    let details = element?.closest('details');
    while (details && this._container.contains(details)) {
      details.open = true;
      details = details.parentElement?.closest('details');
    }
  }

  _bindPageSelector() {
    const select = this._container.querySelector('[data-action="pb-project-page"]');
    if (!select) return;
    select.addEventListener('change', () => {
      const newId = select.value;
      const state = store.getState();
      if (!newId || newId === state?.activePage) return;
      // Switching the active page also re-renders the iframe, but it's the
      // closest one-click affordance to "edit SEO for another page" without
      // a deeper SEOPanel refactor (deferred to a follow-up release).
      store.update('activePage', newId);
    });
  }

  _bindResyncButton() {
    const btn = this._container.querySelector('[data-action="pb-resync-client"]');
    if (!btn) return;
    btn.addEventListener('click', () => this._runResync());
  }

  /**
   * The two dialogs that used to open from the ⋮ menu (CMD-8). Entry points
   * only: `shell.js` still owns both modals and neither changed, so this is a
   * relocation of the door, not of the room.
   */
  _bindAdvancedButtons() {
    for (const [action, command] of [
      ['pb-open-code-injection', 'toolbar:open-code-injection'],
      ['pb-open-link-manager', 'toolbar:open-link-manager']
    ]) {
      this._container
        .querySelector(`[data-action="${action}"]`)
        ?.addEventListener('click', () => eventBus.emit(command));
    }
  }

  _getImageSourceSetting(key) {
    return IMAGE_SOURCE_SETTINGS.find((source) => source.key === key) || null;
  }

  _setImageSourceStatus(key, hasValue) {
    const row = this._container.querySelector(`[data-image-source-row="${key}"]`);
    const status = this._container.querySelector(`[data-image-source-status="${key}"]`);
    if (row) row.dataset.saved = hasValue ? 'true' : 'false';
    if (status) status.textContent = hasValue ? 'Saved for this account' : 'Not set';
  }

  _bindImageSourceSettings() {
    for (const input of this._container.querySelectorAll('[data-image-source-key]')) {
      const key = input.dataset.imageSourceKey;
      const source = this._getImageSourceSetting(key);
      if (!source) continue;
      input.addEventListener('input', () => {
        source.adapter.setApiKey(input.value);
        this._setImageSourceStatus(key, !!input.value.trim());
        eventBus.emit('image-sources:settings-changed', { provider: key });
      });
    }

    for (const btn of this._container.querySelectorAll('[data-image-source-clear]')) {
      const key = btn.dataset.imageSourceClear;
      const source = this._getImageSourceSetting(key);
      if (!source) continue;
      btn.addEventListener('click', () => {
        const input = this._container.querySelector(`[data-image-source-key="${key}"]`);
        source.adapter.setApiKey('');
        if (input) {
          input.value = '';
          input.focus({ preventScroll: true });
        }
        this._setImageSourceStatus(key, false);
        eventBus.emit('image-sources:settings-changed', { provider: key });
      });
    }
  }

  _runResync() {
    // B5: close any in-flight input transaction and drop focus before we
    // start our own batch transaction.
    store.commitTransaction();
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }

    const state = store.getState();
    const { updates, report } = migrateAllSectionsInState(state);
    const nextSnapshot = clientInfoToSourceProfile(state.clientInfo, state.sourceClientProfile);
    const snapshotChanged =
      JSON.stringify(nextSnapshot) !== JSON.stringify(state.sourceClientProfile || {});
    const working = structuredClone(state);
    for (const update of updates) setByPath(working, update.path, update.html);
    if (snapshotChanged) working.sourceClientProfile = nextSnapshot;
    const contextChanged = refreshCompositionContextFromClientInfo(working);
    const semanticReport = resyncComposition(working);
    const pagesChanged = JSON.stringify(working.pages) !== JSON.stringify(state.pages);
    const sharedChanged =
      JSON.stringify(working.sharedSections) !== JSON.stringify(state.sharedSections);

    if (!pagesChanged && !sharedChanged && !snapshotChanged && !contextChanged) {
      showToast('No sections changed — everything is already bound.', 'info');
      return;
    }

    store.beginTransaction('Resync sections from Client Info');
    if (pagesChanged) store.update('pages', working.pages, 'Resync sections from Client Info');
    if (sharedChanged)
      store.update('sharedSections', working.sharedSections, 'Resync sections from Client Info');
    // Snapshot: roll the sourceClientProfile forward to match the now-current
    // client values. The next edit-then-resync uses these as the "old" values.
    // Single transaction with the section updates so Cmd-Z reverts both.
    if (snapshotChanged) {
      store.update('sourceClientProfile', nextSnapshot, 'Resync sections from Client Info');
    }
    if (contextChanged) {
      store.update(
        'compositionContext',
        working.compositionContext,
        'Resync sections from Client Info'
      );
    }
    store.commitTransaction();

    const parts = [];
    if (report.counts.phone)
      parts.push(`${report.counts.phone} phone${report.counts.phone === 1 ? '' : 's'}`);
    if (report.counts.email)
      parts.push(`${report.counts.email} email${report.counts.email === 1 ? '' : 's'}`);
    if (report.counts.address)
      parts.push(`${report.counts.address} address${report.counts.address === 1 ? '' : 'es'}`);
    if (report.counts.social)
      parts.push(`${report.counts.social} social link${report.counts.social === 1 ? '' : 's'}`);
    if (report.counts.mapLink) parts.push(`${report.counts.mapLink} map link`);
    if (report.counts.domain) parts.push(`${report.counts.domain} domain`);
    if (report.counts.name) parts.push(`${report.counts.name} business name`);
    if (report.counts.tagline) parts.push(`${report.counts.tagline} tagline`);
    if (semanticReport.sectionsChanged)
      parts.push(
        `${semanticReport.sectionsChanged} semantic section${semanticReport.sectionsChanged === 1 ? '' : 's'}`
      );
    const summary = parts.length ? parts.join(', ') : 'updates';
    showToast(`Bound ${summary} to Client Info.`, 'success');
  }

  _bindClientInputs() {
    for (const input of this._container.querySelectorAll('[data-client-path]')) {
      const path = input.dataset.clientPath;
      if (!path) continue;
      const fieldLabel = path.split('.').slice(1).join(' ');
      const txnLabel = `Edit client ${fieldLabel}`;

      input.addEventListener('focus', () => store.beginTransaction(txnLabel));
      input.addEventListener('blur', () => store.commitTransaction());
      input.addEventListener('input', () => {
        store.update(path, input.value, txnLabel);
      });
    }
  }

  /**
   * Inputs bound to a full-state path rather than to clientInfo. The
   * [data-client-path] pair reads through normalizeClientInfo(), which knows
   * nothing about state.deploy — these read and write the raw state instead.
   */
  _bindStateInputs() {
    for (const input of this._container.querySelectorAll('[data-state-path]')) {
      const path = input.dataset.statePath;
      if (!path) continue;
      const txnLabel = `Edit ${path.split('.').pop()}`;

      input.addEventListener('focus', () => store.beginTransaction(txnLabel));
      input.addEventListener('blur', () => store.commitTransaction());
      input.addEventListener('input', () => {
        store.update(path, input.value, txnLabel);
      });
    }
  }

  _ensureSeoPanel() {
    if (this._seoPanel || !this._seoMount) return;
    if (!this._hasSeoState()) return;
    this._seoPanel = new SEOPanel(this._seoMount);
  }

  _syncClientInputs() {
    const client = this._getClientInfo();
    const state = store.getState();
    const projectName = state?.projectName || 'Untitled';

    for (const input of this._container.querySelectorAll('[data-client-path]')) {
      const path = input.dataset.clientPath;
      if (!path) continue;
      const value = getByPath(client, path.replace(/^clientInfo\./, ''));
      if (document.activeElement !== input && input.value !== value) {
        input.value = value;
      }
      if (path === 'clientInfo.name' && !input.value) {
        input.placeholder = projectName;
      }
    }

    for (const input of this._container.querySelectorAll('[data-state-path]')) {
      const path = input.dataset.statePath;
      if (!path) continue;
      const value = getByPath(state, path);
      if (document.activeElement !== input && input.value !== value) {
        input.value = value;
      }
    }

    // Phase 8 — keep the page-selector chip in sync with state.activePage.
    // (Option list only refreshes on full re-render; that's acceptable since
    // page add/remove is rare while editing SEO.)
    const select = this._container.querySelector('[data-action="pb-project-page"]');
    if (select && state?.activePage && select.value !== state.activePage) {
      select.value = state.activePage;
    }

    this._syncImageSourceInputs();
  }

  _syncImageSourceInputs() {
    for (const source of IMAGE_SOURCE_SETTINGS) {
      const input = this._container.querySelector(`[data-image-source-key="${source.key}"]`);
      if (!input) continue;
      const value = source.adapter.getApiKey();
      if (document.activeElement !== input && input.value !== value) {
        input.value = value;
      }
      this._setImageSourceStatus(source.key, !!value);
    }
  }

  _focusImageSourceSettings(provider) {
    const requested = typeof provider === 'string' ? provider : '';
    const source = this._getImageSourceSetting(requested) || IMAGE_SOURCE_SETTINGS[0];
    if (!source) return false;
    const input = this._container.querySelector(`[data-image-source-key="${source.key}"]`);
    const row = this._container.querySelector(`[data-image-source-row="${source.key}"]`);
    if (!input) return false;
    this._openTaskFor(input);
    if (row && typeof row.scrollIntoView === 'function') {
      row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    input.focus({ preventScroll: true });
    if (typeof input.select === 'function') input.select();
    return true;
  }

  _listen() {
    this._onStateChanged = () => {
      if (this._destroyed) return;
      this._syncClientInputs();
      this._ensureSeoPanel();
    };
    eventBus.on('state-changed', this._onStateChanged);

    this._onFocusField = (payload = {}) => {
      if (this._destroyed) return;
      const path = typeof payload.path === 'string' ? payload.path : '';
      if (!path) return;
      const input = this._container.querySelector(`[data-client-path="${path}"]`);
      if (!input) return;
      this._openTaskFor(input);
      input.focus({ preventScroll: false });
      if (typeof input.select === 'function') input.select();
    };
    eventBus.on('client-seo:focus-field', this._onFocusField);

    this._onFocusSeoField = (payload = {}) => {
      if (this._destroyed) return;
      const state = store.getState();
      const pageId = typeof payload.pageId === 'string' ? payload.pageId : '';
      if (pageId && pageId !== state?.activePage) {
        store.update('activePage', pageId, 'Switch page');
      }
      this._ensureSeoPanel();
      this._openTaskFor(this._seoMount);
      setTimeout(() => {
        if (this._destroyed) return;
        this._ensureSeoPanel();
        this._seoPanel?.focusField?.(payload.fieldKey || 'title');
      }, 0);
    };
    eventBus.on('client-seo:focus-seo-field', this._onFocusSeoField);

    this._onFocusResync = () => {
      if (this._destroyed) return;
      const btn = this._container.querySelector('[data-action="pb-resync-client"]');
      if (btn) {
        this._openTaskFor(btn);
        btn.focus({ preventScroll: false });
      }
    };
    eventBus.on('client-seo:focus-resync', this._onFocusResync);

    this._onFocusImageSourceSettings = (payload = {}) => {
      if (this._destroyed) return;
      const provider = typeof payload.provider === 'string' ? payload.provider : '';
      if (this._focusImageSourceSettings(provider)) return;
      setTimeout(() => {
        if (!this._destroyed) this._focusImageSourceSettings(provider);
      }, 0);
    };
    eventBus.on('client-seo:focus-image-source-settings', this._onFocusImageSourceSettings);
  }

  destroy() {
    this._destroyed = true;
    store.commitTransaction();
    if (this._seoPanel) {
      this._seoPanel.destroy();
      this._seoPanel = null;
    }
    eventBus.off('state-changed', this._onStateChanged);
    eventBus.off('client-seo:focus-field', this._onFocusField);
    eventBus.off('client-seo:focus-seo-field', this._onFocusSeoField);
    eventBus.off('client-seo:focus-resync', this._onFocusResync);
    eventBus.off('client-seo:focus-image-source-settings', this._onFocusImageSourceSettings);
  }
}
